<script setup>
import { ref, onMounted, nextTick, computed, watch } from "vue";
import Distelect from "@/utilities/Distelec.json";
import { useStore } from "vuex";
import Swal from "sweetalert2";

import UpdateUserModal from "@/views/Users/UpdateUser.vue";
import UpdatePasswordModal from "@/views/Users/UpdatePassword.vue";
import NewUser from "@/views/Users/NewUser.vue";
import DeleteUser from "@/views/auth/domain/delete-user"; 
const userRole = sessionStorage.getItem("usuarioRol"); 

import GetUsersFromOrg from "@/views/auth/domain/get-users-from-org";
import { DataTable } from "simple-datatables";

const store = useStore();

const headers = ["ID", "Nombre", "Correo", "Telefono", "Rol", "Provincia", "Canton", "Distrito", "Junta"];
const users = ref([]);
const error = ref(null);
const selectedUserCedula = ref(null);
const selectedUserName = ref(null);
const selectedUserEmail = ref(null);
const selectedUserPhone= ref(null);
const selectedUserRol = ref(null);
const selectedUserJunta = ref(null);
const selectedUserProvincia = ref(null);
const selectedUserCanton = ref(null);
const selectedUserDistrito = ref(null);

const loading = ref(true);

const showUserModal = ref(false);
const showPasswordModal = ref(false);
const showNewUserModal = ref(false);

const openNewUserModal = () => {
  showNewUserModal.value = true;
};

if (window.innerWidth <= 768) {
  store.commit("navbarMinimize");
}

const selectedProvincia = ref("");
const selectedCanton = ref("");
const selectedDistrito = ref("");

const provincias = ref(
  Object.entries(Distelect.provincias).map(([key, value]) => ({ id: key, nombre: value.nombre }))
);
const cantones = ref([]);
const distritos = ref([]);

const filteredUsers = computed(() => {
  return users.value.filter((user) => {
    const provinciaMatch = !selectedProvincia.value || user.provinciaId === selectedProvincia.value;
    const cantonMatch = !selectedCanton.value || user.cantonId === selectedCanton.value;
    const distritoMatch = !selectedDistrito.value || user.distritoId === selectedDistrito.value;
    return provinciaMatch && cantonMatch && distritoMatch;
  });
});

//Obtiene los usuarios de la organización
const fetchUsers = async () => {
  try {
    const response = await GetUsersFromOrg();
    users.value = response;
  } catch (err) {
    error.value = "No se pudieron cargar los usuarios.";
  } finally {
    loading.value = false;
  }
};

const handleUserDelete = async (userCedula) => {
  try {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "¡Este usuario será eliminado permanentemente!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      await DeleteUser(userCedula); // Llama a eliminar usuario por cedula
      
      Swal.fire(
        'Eliminado!',
        'El usuario ha sido eliminado.',
        'success'
      );
      
      location.reload();
    }
  } catch (err) {
    console.error("Error eliminando usuario", err);
    Swal.fire(
      'Error',
      'Hubo un problema al eliminar al usuario. Por favor, intenta de nuevo.',
      'error'
    );
  }
}

onMounted(async () => {
  store.commit("navbarMinimize");
  await fetchUsers();
  await nextTick();

  const tableElement = document.getElementById("users-list");
  if (tableElement) {
    new DataTable(tableElement, {
      searchable: true,
      fixedHeight: true,
      perPage: 10,
      labels: {
        placeholder: "Buscar usuario...",
        perPage: "{select} registros por página",
        noRows: "No se encontraron registros",
        info: "Mostrando {start} al {end} de {rows} registros",
      },
    });

    tableElement.addEventListener("click", (event) => {
      if (event.target.classList.contains("edit-action")) {
        const row = event.target.closest("tr"); 
        if (row) {
          //Asume que es el orden de las columnas para extraer los datos
          const userId = row.children[0].textContent.trim(); 
          const userName = row.children[1].textContent.trim();
          const userEmail = row.children[2].textContent.trim(); 
          const userPhone = row.children[3].textContent.trim(); 
          const userRol = row.children[4].textContent.trim(); 
          const userProvincia= row.children[5].textContent.trim(); 
          const userCanton = row.children[6].textContent.trim(); 
          const userDistrito = row.children[7].textContent.trim(); 
          const userJunta = row.children[8].textContent.trim(); 

          openModal(userId, userEmail,userName,userPhone,userRol, userProvincia, userCanton, userDistrito, userJunta);
        }
      }
      if (event.target.classList.contains("delete-action")) {
        const row = event.target.closest("tr"); 

        const cedula = row.children[0].textContent.trim();
        handleUserDelete(cedula);     
        
      }
      
      
    });
  }
});

const openModal = (userId, userEmail,userName,userPhone,userRol, userProvincia, userCanton, userDistrito, userJunta) => {
  selectedUserCedula.value = userId;
  selectedUserEmail.value = userEmail;
  selectedUserName.value = userName;
  selectedUserPhone.value = userPhone;
  selectedUserRol.value = userRol;

  selectedUserProvincia.value = userProvincia;
  selectedUserCanton.value = userCanton;
  selectedUserDistrito.value = userDistrito;

  selectedUserJunta.value = userJunta;

  showUserModal.value = true;
};


const closeModal = () => {
  showUserModal.value = false;
  showPasswordModal.value = false;
  showNewUserModal.value = false;
};

watch(selectedProvincia, (newProvincia) => {
  if (newProvincia) {
    const provinciaData = Distelect.provincias[newProvincia];
    cantones.value = Object.entries(provinciaData.cantones).map(([key, value]) => ({ id: key, nombre: value.nombre }));
    selectedCanton.value = "";
    selectedDistrito.value = "";
    distritos.value = [];
  } else {
    cantones.value = [];
    distritos.value = [];
  }
});

watch(selectedCanton, (newCanton) => {
  if (newCanton && selectedProvincia.value) {
    const cantonData = Distelect.provincias[selectedProvincia.value].cantones[newCanton];
    distritos.value = Object.entries(cantonData.distritos).map(([key, nombre]) => ({ id: key, nombre }));
    selectedDistrito.value = "";
  } else {
    distritos.value = [];
  }
});

</script>

<template>
  <div class="container-fluid py-4 mh-600">
    <div class="row mt-4">
      <div v-if="loading" class="text-center">
        <p>Cargando...</p>
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>

      <div v-else class="card">
        <div class="p-3 pb-0 card-header">
          <div class="d-flex justify-content-between">
            <h6 class="mb-2"><i class="fas fa-users pe-2"></i>Usuarios</h6>
            <button class="btn btn-success btn-sm" @click="openNewUserModal">
              + Nuevo Usuario
            </button>          
          </div>
        </div>

        <div class="table-responsive pb-4">
          <table id="users-list" class="table align-items-center mb-0">
            <thead>
              <tr>
                <th
                  v-for="(header, index) in headers"
                  :key="index"
                  :class="`text-uppercase text-dark text-xxs font-weight-bolder opacity-7 ${index !== 0 ? 'ps-2' : ''}`"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="user in filteredUsers"
                :key="user.usuarioId"
                class="row-hover"
              >
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.id_usuario }}
                  </p>
                </td>
                <td class="px-2 py-1">
                  <div class="d-flex align-items-center">
                    <div class="d-flex flex-column justify-content-center">
                      <h6 class="mb-0 text-xs">{{ user.nombre_completo }}</h6>                                        
                    </div>
                  </div>
                </td>
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.email }}
                  </p>
                </td>
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.telefono }}
                  </p>
                </td>    
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.rol === "administrador" ? "Administrador" 
                      : user.rol === "dueno" ? "Dueño" 
                      : "Colaborador" }} 

                  </p>
                </td>          
             
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.distelec.provincia }}
                  </p>
                </td>
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.distelec.canton }}
                  </p>
                </td>
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.distelec.distrito }}
                  </p>
                </td>
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.junta }}
                  </p>
                </td>
                <td v-if="user.rol !== 'dueno'" class="align-middle">
                  <div v-if="userRole === 'dueno' || (user.rol === 'usuario' && userRole != 'usuario') " class="dropdown-xs w-40">
                    <button
                      class="btn btn-link text-xs text-success dropdown-toggle mb-0"
                      type="button"
                      id="dropdownMenuButton"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      Editar
                    </button>             
                    <ul
                      class="dropdown-menu w-20"
                      aria-labelledby="dropdownMenuButton"
                    >
                      <li>
                        <a
                            class="dropdown-item edit-action"
                            :data-cedula="user.usuarioCedula"
                            data-type="user"
                            ><i class="fas fa-user-edit pe-2"></i>Editar Datos</a
                          >
                      </li>
                      <li>
                        <a
                          class="dropdown-item delete-action text-danger "
                          :data-cedula="user.usuarioCedula"
                          data-type="user"

                        >
                      
                        <i class="fas fa-user-times pe-2"></i>Eliminar Usuario
                        </a>
                      </li>            
                    </ul>
                  </div>
                  <div v-else  class="d-flex justify-content-center align-middle"> <p class="text-xs font-weight-bold mb-0">
                    {{ user.rol === "administrador" ? "Admin"                       
                      : "Colaborador" }} 

                  </p><i class="far fa-star ps-2"></i></div>
                </td>
                <td v-else class="d-flex justify-content-center align-middle"> <p class="text-xs font-weight-bold mb-0">
                    {{  user.rol === "dueno" ? "Dueño" 
                      : "Colaborador" }} 

                  </p><i class="fas fa-star ps-2"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modales -->
    <UpdateUserModal 
      v-if="showUserModal"
      :id="selectedUserCedula" 
      :name="selectedUserName" 
      :email="selectedUserEmail"
      :telefono="selectedUserPhone"
      :rol="selectedUserRol"
      :junta="selectedUserJunta"
      :provincia="selectedUserProvincia"
      :canton="selectedUserCanton"
      :distrito="selectedUserDistrito"
      @close="closeModal"
    />


    <update-password-modal
      v-if="showPasswordModal"
      :cedula="selectedUserCedula"
      @close="closeModal"
    />

    <new-user
      v-if="showNewUserModal"
      @close="closeModal"
    />
  </div>
</template>


<style scoped>
.row-hover {
  transition: background-color 0.3s ease;
}
.row-hover:hover {
  background-color: #f0f4f8;
}
.spinner-border {
  width: 3rem;
  height: 3rem;
  margin: 2rem auto;
  color: #f0f4f8;
}
</style>
